import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { RootState } from '../../../redux/reducers';
import { formatDate } from '../../../utils/stringUtils';
import {
  BankTransactionParticipation,
  ContributionPlanWithMembersModel,
  CreateBankTransactionModel,
  TransactionType,
  BankMutation,
  BankTransactionWithParticipation,
} from '../../../types/bankAccount';
import Button from '../buttons/Button';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import ModalLayout from '../layouts/ModalLayout';
import Labeled from '../misc/Labeled';

import DropdownSelect from '../forms/DropdownSelect';
import {
  formatEuroCentsWithSign,
  matchMutationsToConnections,
} from '../../../utils/bankAccountUtil';
import CurrencyInput from '../forms/CurrencyInput';
import SplitMembersList from '../financial/SplitMembersList';

import Slider from '../misc/Slider';
import * as bankAccountService from '../../../services/bankAccountService';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import TransactionProcessor from '../financial/TransactionProcessor';

// Helper function to convert BankTransactionWithParticipation to CreateBankTransactionModel
function convertToCreateModel(
  transaction: BankTransactionWithParticipation,
): CreateBankTransactionModel {
  return {
    amount: transaction.amount,
    description: transaction.description,
    date: transaction.date,
    mutationId: transaction.mutationId,
    payerId: transaction.payerId,
    transactionType: transaction.transactionType,
    participations: transaction.participations || undefined,
    contributionPayment: transaction.contributionPayment,
  };
}

interface SplitModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialTransaction?: CreateBankTransactionModel | BankTransactionWithParticipation;
  mutation?: BankMutation;
  onSplitSuccess?: (transaction: CreateBankTransactionModel | CreateBankTransactionModel[]) => void;
  initialProcessType?: ProcessType;
  initialInternalSide?: InternalSide;
  onInternalCreate?: (params: {
    fromMutationId: number;
    toMutationId: number;
    description?: string;
  }) => void;
}

type ProcessType = 'ASSIGN' | 'SPLIT' | 'INTERNAL';
type AssignmentType = 'ENTIRE_GROUP' | 'INDIVIDUALS';
type ContributionType = 'YES' | 'NO';
type InternalSide = 'FROM' | 'TO';

type BankTransactionState = {
  description: string;
  date: Date;
  amount: number;
  transactionType?: TransactionType;
  payerId?: number;
  mutationId?: number;
  selectedMembers: number[];
  memberValues: Record<number, number>;
  memberRations?: Record<number, number | undefined>;
  contributionPayment?: {
    contributionPlanId?: number;
    connectionId?: number;
  };
  processType?: ProcessType;
  assignmentType?: AssignmentType;
  isContributionPayment?: ContributionType;
  // Split functionality state
  splitTransactions?: CreateBankTransactionModel[];
  // Internal transfer state
  internalTransferSide?: InternalSide;
  internalCandidates?: BankMutation[];
  internalSelectedCounterpartId?: number;
};

const getEmptyTransactionState = (
  mutation?: BankMutation,
  initialTransaction?: CreateBankTransactionModel,
) => {
  const transactionInfo = mutation || initialTransaction;
  return {
    description: '',
    date: new Date(),
    amount: transactionInfo?.amount ?? 0,
    selectedMembers: [],
    memberValues: {},
    memberRations: {},
    assignmentType: undefined,
    isContributionPayment: undefined,
    processType: 'ASSIGN' as ProcessType, // Auto-select ASSIGN
  };
};

const transformTransactionModelIntoState = (tns: CreateBankTransactionModel) => {
  // Determine assignment type and contribution type based on transaction type
  let assignmentType: AssignmentType | undefined;
  let isContributionPayment: ContributionType | undefined;

  if (tns.transactionType.startsWith('GROUP')) {
    assignmentType = 'ENTIRE_GROUP';
  } else if (tns.transactionType.startsWith('PERSONAL')) {
    assignmentType = 'INDIVIDUALS';
    isContributionPayment = 'NO';
  } else if (tns.transactionType === TransactionType.CONTRIBUTION) {
    assignmentType = 'INDIVIDUALS';
    isContributionPayment = 'YES';
  }

  // If we have contribution payment data, override the assignment type
  if (tns.contributionPayment) {
    assignmentType = 'INDIVIDUALS';
    isContributionPayment = 'YES';
  }

  // Calculate member rations from existing participation amounts
  const calculateMemberRations = (participations: BankTransactionParticipation[] | undefined) => {
    if (!participations || participations.length === 0) return {};

    const memberRations: Record<number, number | undefined> = {};
    const totalAmount = Math.abs(tns.amount);

    // Check if amounts are proportional (within 1 cent tolerance)
    const amounts = participations.map((p) => Math.abs(p.amount));
    const totalParticipationAmount = amounts.reduce((sum, amount) => sum + amount, 0);

    // Only calculate rations if the total participation amount matches the transaction amount
    if (Math.abs(totalParticipationAmount - totalAmount) <= 1) {
      // Find the minimum amount to use as the base unit
      const minAmount = Math.min(...amounts);

      // Calculate rations based on the minimum amount
      participations.forEach((participation) => {
        const amount = Math.abs(participation.amount);
        const ration = amount / minAmount;

        // Round to the nearest whole number if it's close enough
        const roundedRation = Math.round(ration);
        const tolerance = 0.1; // 10% tolerance for rounding

        if (Math.abs(ration - roundedRation) <= tolerance) {
          memberRations[participation.connectionId] = roundedRation;
        } else {
          memberRations[participation.connectionId] = undefined;
        }
      });
    }

    return memberRations;
  };

  return {
    ...tns,
    contributionPayment: tns.contributionPayment ?? undefined,
    selectedMembers: tns.participations?.map((p) => p.connectionId) ?? [],
    memberValues:
      tns.participations?.reduce(
        (acc, p) => {
          acc[p.connectionId] = p.amount;
          return acc;
        },
        {} as Record<number, number>,
      ) ?? {},
    memberRations: calculateMemberRations(tns.participations),
    assignmentType,
    isContributionPayment,
    processType: 'ASSIGN' as ProcessType, // Always set ASSIGN as default
  };
};

export default function TransactionModal({
  open,
  setOpen,
  onSplitSuccess,
  onInternalCreate,
  initialTransaction,
  mutation,
  initialProcessType,
  initialInternalSide,
}: SplitModalProps) {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.splitModal' });
  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.B2C_CONNECTION);
  const [transaction, setTransaction] = useState<BankTransactionState>({
    ...getEmptyTransactionState(
      mutation,
      initialTransaction && 'transactionId' in initialTransaction
        ? convertToCreateModel(initialTransaction)
        : initialTransaction,
    ),
  });
  const [remainingAmount, setRemainingAmount] = useState(initialTransaction?.amount ?? 0);
  const [plans, setPlans] = useState<Record<string, ContributionPlanWithMembersModel>>({});
  const [bankAccountsCount, setBankAccountsCount] = useState<number | undefined>(undefined);

  // Get connections that are part of at least one contribution plan
  const connectionsWithPlans = useMemo(() => {
    return connections.filter((connection) => {
      return Object.values(plans).some((plan) =>
        plan.appliesTo.some((member) => member.connectionId === +connection.id),
      );
    });
  }, [connections, plans]);

  // Get contribution plans for a selected connection
  const getContributionPlansForConnection = useMemo(() => {
    return (connectionId: number) => {
      return Object.values(plans).filter((plan) =>
        plan.appliesTo.some((member) => member.connectionId === connectionId),
      );
    };
  }, [plans]);

  // Auto-select contribution plan if connection is part of only one plan
  useEffect(() => {
    if (
      transaction.contributionPayment?.connectionId &&
      !transaction.contributionPayment?.contributionPlanId
    ) {
      const availablePlans = getContributionPlansForConnection(
        transaction.contributionPayment.connectionId,
      );
      if (availablePlans.length === 1) {
        setTransaction((prevTns) => ({
          ...prevTns,
          contributionPayment: {
            ...prevTns.contributionPayment,
            contributionPlanId: availablePlans[0].contributionPlanId,
          },
        }));
      }
    }
  }, [transaction.contributionPayment?.connectionId, getContributionPlansForConnection]);
  const editMode = useMemo(() => !!initialTransaction, [initialTransaction]);
  const business = useSelectedBusiness();

  useEffect(() => {
    bankAccountService.getAllContributionPlans().then((ps) => {
      setPlans(_.keyBy(ps, (p) => p.contributionPlanId));
    });
  }, [business?.businessId]);

  useEffect(() => {
    if (!initialTransaction) {
      setTransaction({ ...getEmptyTransactionState(mutation, initialTransaction) });
      // remaining amount derives from amount + memberValues
    } else {
      const transactionToUse =
        'transactionId' in initialTransaction
          ? convertToCreateModel(initialTransaction)
          : initialTransaction;
      setTransaction(transformTransactionModelIntoState(transactionToUse));
      setRemainingAmount(initialTransaction.amount);
    }
  }, [initialTransaction, mutation]);

  // Set transaction amount from mutation or initial transaction
  useEffect(() => {
    // Avoid conflicting with initialTransaction bootstrap effect
    if (initialTransaction) return;
    const transactionInfo = mutation || initialTransaction;
    if (transactionInfo) {
      setTransaction((prevTns) => {
        const desired = transactionInfo.amount;
        if (prevTns.amount === desired) return prevTns;
        return {
          ...prevTns,
          amount: desired,
        };
      });
    }
  }, [mutation, initialTransaction]);

  // Set initial assignment type and contribution type when amount changes (for new transactions)
  useEffect(() => {
    if (initialTransaction) return; // only for new transactions
    if (transaction.amount !== 0) {
      const isExpense = transaction.amount < 0;

      // Set default assignment type based on amount
      if (!transaction.assignmentType) {
        setTransaction((prevTns) => ({
          ...prevTns,
          assignmentType: 'ENTIRE_GROUP',
        }));
      }

      // Set default transaction type based on assignment type
      if (transaction.assignmentType && !transaction.transactionType) {
        const newTransactionType =
          transaction.assignmentType === 'ENTIRE_GROUP'
            ? isExpense
              ? TransactionType.GROUP_EXPENSE
              : TransactionType.GROUP_INCOME
            : isExpense
              ? TransactionType.PERSONAL_EXPENSE
              : TransactionType.PERSONAL_INCOME;

        setTransaction((prevTns) => {
          if (prevTns.transactionType === newTransactionType) return prevTns;
          return {
            ...prevTns,
            transactionType: newTransactionType,
          };
        });
      }
    }
  }, [transaction.amount, transaction.assignmentType, initialTransaction]);

  useEffect(() => {
    if (
      transaction.transactionType &&
      (transaction.transactionType.startsWith('GROUP') ||
        transaction.transactionType === TransactionType.CONTRIBUTION)
    ) {
      setTransaction((prevTns) => {
        if (
          prevTns.selectedMembers.length === 0 &&
          Object.keys(prevTns.memberValues).length === 0
        ) {
          return prevTns;
        }
        return {
          ...prevTns,
          selectedMembers: [],
          memberValues: {},
        };
      });
      setRemainingAmount(0);
    }
  }, [transaction.transactionType]);

  // Set initial assignment type and contribution type based on transaction type
  useEffect(() => {
    if (transaction.transactionType) {
      if (transaction.transactionType.startsWith('GROUP')) {
        setTransaction((prevTns) => ({
          ...prevTns,
          assignmentType: 'ENTIRE_GROUP',
        }));
      } else if (transaction.transactionType.startsWith('PERSONAL')) {
        setTransaction((prevTns) => ({
          ...prevTns,
          assignmentType: 'INDIVIDUALS',
          isContributionPayment: 'NO',
        }));
      } else if (transaction.transactionType === TransactionType.CONTRIBUTION) {
        setTransaction((prevTns) => ({
          ...prevTns,
          assignmentType: 'INDIVIDUALS',
          isContributionPayment: 'YES',
        }));
      }
    }
  }, [transaction.transactionType, transaction.amount]);

  // Auto-match income transactions to connections and contribution plans
  useEffect(() => {
    // Only auto-match when we haven't chosen an assignment yet
    if (transaction.assignmentType) return;
    if (
      mutation &&
      transaction.amount > 0 &&
      (!initialTransaction || !initialTransaction.contributionPayment)
    ) {
      // Try to match the mutation to a connection
      const matches = matchMutationsToConnections([mutation], connections);
      const match = matches[0];

      if (match.matchedConnection) {
        // Found a matching connection, set up for individual assignment
        setTransaction((prevTns) => {
          const desiredId = +match.matchedConnection!.id;
          const alreadySelected =
            prevTns.selectedMembers.length === 1 && prevTns.selectedMembers[0] === desiredId;
          const alreadyType = prevTns.assignmentType === 'INDIVIDUALS';
          if (alreadySelected && alreadyType) return prevTns;
          return {
            ...prevTns,
            assignmentType: 'INDIVIDUALS',
            selectedMembers: [desiredId],
          };
        });

        // Check if this connection has contribution plans
        const connectionPlans = Object.values(plans).filter((plan) =>
          plan.appliesTo.some((member) => member.connectionId === +match.matchedConnection!.id),
        );

        if (connectionPlans.length > 0) {
          // Auto-select the first available contribution plan
          const selectedPlan = connectionPlans[0];

          setTransaction((prevTns) => {
            const sameConn =
              prevTns.contributionPayment?.connectionId === +match.matchedConnection!.id;
            const samePlan =
              prevTns.contributionPayment?.contributionPlanId === selectedPlan.contributionPlanId;
            const sameType = prevTns.transactionType === TransactionType.CONTRIBUTION;
            const sameFlag = prevTns.isContributionPayment === 'YES';
            if (sameConn && samePlan && sameType && sameFlag) return prevTns;
            return {
              ...prevTns,
              isContributionPayment: 'YES',
              transactionType: TransactionType.CONTRIBUTION,
              contributionPayment: {
                connectionId: +match.matchedConnection!.id,
                contributionPlanId: selectedPlan.contributionPlanId,
              },
            };
          });
        } else {
          // No contribution plans, set up as regular income
          setTransaction((prevTns) => {
            if (
              prevTns.isContributionPayment === 'NO' &&
              prevTns.transactionType === TransactionType.PERSONAL_INCOME
            ) {
              return prevTns;
            }
            return {
              ...prevTns,
              isContributionPayment: 'NO',
              transactionType: TransactionType.PERSONAL_INCOME,
            };
          });
        }
      } else {
        // No matching connection found, default to entire group
        setTransaction((prevTns) => {
          if (prevTns.assignmentType === 'ENTIRE_GROUP') return prevTns;
          return {
            ...prevTns,
            assignmentType: 'ENTIRE_GROUP',
          };
        });
      }
    }
  }, [
    mutation,
    transaction.amount,
    connections,
    plans,
    initialTransaction,
    transaction.assignmentType,
  ]);

  // Auto-select ENTIRE_GROUP for expenses
  useEffect(() => {
    // Only set default ENTIRE_GROUP for expenses if not chosen yet
    if (transaction.assignmentType) return;
    if (
      mutation &&
      transaction.amount < 0 &&
      (!initialTransaction || !initialTransaction.contributionPayment)
    ) {
      setTransaction((prevTns) => ({
        ...prevTns,
        assignmentType: 'ENTIRE_GROUP',
      }));
    }
  }, [mutation, transaction.amount, initialTransaction, transaction.assignmentType]);

  // Ensure processType is always set to ASSIGN
  useEffect(() => {
    if (!transaction.processType) {
      setTransaction((prevTns) => ({
        ...prevTns,
        processType: initialProcessType ?? 'ASSIGN',
      }));
    }
  }, [transaction.processType, initialProcessType]);

  // Initialize internal side if provided
  useEffect(() => {
    if (initialInternalSide && transaction.internalTransferSide !== initialInternalSide) {
      setTransaction((prevTns) => ({
        ...prevTns,
        internalTransferSide: initialInternalSide,
      }));
    }
  }, [initialInternalSide, transaction.internalTransferSide]);

  // Removed auto equal distribution based on selected members; use Split equally button instead

  useEffect(() => {
    if (transaction.transactionType !== TransactionType.CONTRIBUTION) {
      setRemainingAmount(
        Math.abs(transaction.amount) -
          Object.values(transaction.memberValues).reduce((sum, val) => sum + Math.abs(val), 0),
      );
    }
  }, [transaction.amount, transaction.transactionType, transaction.memberValues]);

  // Member selection is no longer used when always-visible rows are shown

  const recalcFromRations = (
    prevTns: BankTransactionState,
    override?: {
      memberValues?: Record<number, number>;
      memberRations?: Record<number, number | undefined>;
    },
  ): BankTransactionState => {
    const { amount } = prevTns;
    const sign = amount < 0 ? -1 : 1;
    const totalAbs = Math.abs(amount);
    const memberValues = override?.memberValues ?? prevTns.memberValues ?? {};
    const memberRations = override?.memberRations ?? prevTns.memberRations ?? {};

    const manualIds = Object.keys(memberValues)
      .map((id) => +id)
      .filter((id) => memberRations[id] === undefined);
    const manualAbsSum = manualIds.reduce((sum, id) => sum + Math.abs(memberValues[id] || 0), 0);
    const remainingAbs = Math.max(0, totalAbs - manualAbsSum);

    const rationEntries = Object.entries(memberRations)
      .map(([k, v]) => ({ id: +k, r: v }))
      .filter((e) => e.r !== undefined);
    const sumRations = rationEntries.reduce((sum, e) => sum + (e.r || 0), 0);

    const newMemberValues: Record<number, number> = { ...memberValues };

    if (sumRations > 0 && remainingAbs > 0) {
      let allocatedAbs = 0;
      rationEntries.forEach((e) => {
        const r = e.r || 0;
        if (r <= 0) {
          newMemberValues[e.id] = 0;
        } else {
          const shareAbs = Math.floor((remainingAbs * r) / sumRations);
          newMemberValues[e.id] = sign * shareAbs;
          allocatedAbs += shareAbs;
        }
      });
      let remainder = remainingAbs - allocatedAbs;
      rationEntries.some((e) => {
        if (remainder <= 0) return true;
        if ((e.r || 0) > 0) {
          newMemberValues[e.id] = (newMemberValues[e.id] || 0) + sign * 1;
          remainder -= 1;
        }
        return false;
      });
    } else {
      rationEntries.forEach((e) => {
        if ((e.r || 0) === 0) newMemberValues[e.id] = 0;
      });
    }

    return {
      ...prevTns,
      memberValues: newMemberValues,
      memberRations,
    };
  };

  const handleValueChange = (memberId: number, value: number) => {
    const totalAmount = transaction.amount;
    const isNegative = totalAmount < 0;
    const sign = isNegative ? -1 : 1;

    setTransaction((prevTns) => {
      const inputCentsAbs = Math.max(0, Math.floor(value * 100));
      const memberRations = { ...(prevTns.memberRations ?? {}) };
      memberRations[memberId] = undefined;

      const pre = recalcFromRations(prevTns, { memberRations });
      const otherMembersTotalAbs = Object.entries(pre.memberValues)
        .filter(([id]) => +id !== memberId)
        .reduce((sum, [, v]) => sum + Math.abs(v || 0), 0);
      const maxForThisMemberAbs = Math.max(0, Math.abs(totalAmount) - otherMembersTotalAbs);
      const clampedAbs = Math.min(maxForThisMemberAbs, inputCentsAbs);

      const updatedMemberValues = {
        ...pre.memberValues,
        [memberId]: sign * clampedAbs,
      };

      const finalState = recalcFromRations(
        { ...pre, memberValues: updatedMemberValues },
        {
          memberValues: updatedMemberValues,
          memberRations,
        },
      );
      return finalState;
    });
  };

  const handleProcessTypeChange = (value: ProcessType) => {
    setTransaction((prevTns) => ({
      ...prevTns,
      processType: value,
    }));

    // Initialize split transactions when Split is selected
    if (value === 'SPLIT') {
      initializeSplitTransactions();
    }
  };

  const handleAssignmentTypeChange = (value: AssignmentType) => {
    setTransaction((prevTns) => ({
      ...prevTns,
      assignmentType: value,
    }));

    // Determine transaction type based on assignment type and amount
    const isExpense = transaction.amount < 0;
    let newTransactionType: TransactionType;

    if (value === 'ENTIRE_GROUP') {
      newTransactionType = isExpense ? TransactionType.GROUP_EXPENSE : TransactionType.GROUP_INCOME;
    } else {
      // INDIVIDUALS
      newTransactionType = isExpense
        ? TransactionType.PERSONAL_EXPENSE
        : TransactionType.PERSONAL_INCOME;
    }

    setTransaction((prevTns) => ({
      ...prevTns,
      transactionType: newTransactionType,
    }));
  };

  const handleContributionTypeChange = (value: ContributionType) => {
    setTransaction((prevTns) => ({
      ...prevTns,
      isContributionPayment: value,
    }));

    // Update transaction type based on contribution choice
    const newTransactionType =
      value === 'YES' ? TransactionType.CONTRIBUTION : TransactionType.PERSONAL_INCOME;

    setTransaction((prevTns) => ({
      ...prevTns,
      transactionType: newTransactionType,
    }));
  };

  const handleInternalSave = () => {
    // Block reassign when editing an existing internal transfer
    const existing = initialTransaction as unknown as
      | { transactionType?: TransactionType }
      | undefined;
    if (existing?.transactionType === TransactionType.INTERNAL_BANK_TRANSFER) {
      return;
    }
    if (!transaction.internalSelectedCounterpartId || !transaction.mutationId) {
      toast.error(t('error.internalCandidates'));
      return;
    }
    const currentIsExpense = (transaction.amount ?? 0) < 0;
    const fromMutationId = currentIsExpense
      ? transaction.mutationId
      : transaction.internalSelectedCounterpartId;
    const toMutationId = currentIsExpense
      ? transaction.internalSelectedCounterpartId
      : transaction.mutationId;
    onInternalCreate?.({ fromMutationId, toMutationId, description: transaction.description });
  };

  // Note: internal side selection no longer used in UI

  // Load internal transfer candidates when switching to INTERNAL mode and preselect linked counterpart if editing
  useEffect(() => {
    if (transaction.processType === 'INTERNAL' && mutation?.mutationId) {
      bankAccountService.getAllBankAccounts().then((acs) => setBankAccountsCount(acs.length));
      bankAccountService
        .getInternalTransferCandidates(mutation.mutationId)
        .then(async (cands) => {
          let preselected: number | undefined;
          const existing = initialTransaction as unknown as
            | { transactionId?: number; transactionType?: TransactionType }
            | undefined;
          if (
            existing?.transactionId &&
            existing.transactionType === TransactionType.INTERNAL_BANK_TRANSFER
          ) {
            try {
              const links = await bankAccountService.listInternalTransfers();
              const link = links.find(
                (l) =>
                  l.fromTransactionId === existing.transactionId ||
                  l.toTransactionId === existing.transactionId,
              );
              if (link) {
                // We can resolve and show details later if needed
              }
            } catch (e) {
              // ignore
            }
          }

          if ((cands?.length || 0) === 1) {
            preselected = cands![0].mutationId;
          }

          setTransaction((prev) => ({
            ...prev,
            internalCandidates: cands ?? [],
            internalSelectedCounterpartId: preselected,
          }));
        })
        .catch(() => toast.error(t('error.internalCandidates')));
    }
  }, [transaction.processType, mutation?.mutationId]);

  // Resolve counterpart transaction id for existing internal transfer
  const [counterpartTransaction, setCounterpartTransaction] = useState<
    BankTransactionWithParticipation | undefined
  >();
  useEffect(() => {
    const existing = initialTransaction as unknown as
      | { transactionId?: number; transactionType?: TransactionType }
      | undefined;
    if (
      existing?.transactionId &&
      existing.transactionType === TransactionType.INTERNAL_BANK_TRANSFER
    ) {
      (async () => {
        try {
          const links = await bankAccountService.listInternalTransfers();
          const link = links.find(
            (l) =>
              l.fromTransactionId === existing.transactionId ||
              l.toTransactionId === existing.transactionId,
          );
          if (!link) return;
          const otherId =
            link.fromTransactionId === existing.transactionId
              ? link.toTransactionId
              : link.fromTransactionId;
          if (!otherId) return;
          const allTx = await bankAccountService.getAllTransactions();
          const otherTx = allTx.find((tx) => tx.transactionId === otherId);
          if (otherTx) setCounterpartTransaction(otherTx);
        } catch {
          // silent
        }
      })();
    }
  }, [initialTransaction]);

  // Split functionality handlers
  const initializeSplitTransactions = () => {
    const transactionType =
      transactionAmount >= 0 ? TransactionType.GROUP_INCOME : TransactionType.GROUP_EXPENSE;

    const splitTransactions = [
      {
        mutationId: transaction.mutationId,
        transactionType,
        amount: 0,
        date: transactionDate,
        description: `${transactionDescription} #1`,
      },
      {
        mutationId: transaction.mutationId,
        transactionType,
        amount: 0,
        date: transactionDate,
        description: `${transactionDescription} #2`,
      },
    ];

    setTransaction((prevTns) => ({
      ...prevTns,
      splitTransactions,
      splitStep: 'details',
    }));
  };

  const addSplitTransaction = () => {
    if (!transaction.splitTransactions) return;

    const transactionType =
      transactionAmount >= 0 ? TransactionType.GROUP_INCOME : TransactionType.GROUP_EXPENSE;
    const newTransaction = {
      mutationId: transaction.mutationId,
      transactionType,
      amount: 0,
      date: transactionDate,
      description: `${transactionDescription} #${transaction.splitTransactions.length + 1}`,
    };

    setTransaction((prevTns) => ({
      ...prevTns,
      splitTransactions: [...(prevTns.splitTransactions || []), newTransaction],
    }));
  };

  const removeSplitTransaction = (index: number) => {
    if (!transaction.splitTransactions || transaction.splitTransactions.length <= 2) return;

    setTransaction((prevTns) => ({
      ...prevTns,
      splitTransactions: prevTns.splitTransactions
        ?.filter((item, idx) => idx !== index)
        .map((tns, idx) => ({
          ...tns,
          description: `${transactionDescription} #${idx + 1}`,
        })),
    }));
  };

  const handleSplitTransactionChange = (
    index: number,
    field: 'description' | 'amount',
    value: string | number,
  ) => {
    setTransaction((prevTns) => {
      if (!prevTns.splitTransactions) return prevTns;

      const newSplitTransactions = [...prevTns.splitTransactions];
      if (field === 'amount') {
        // Ensure the amount has the same sign as the original mutation
        const signedAmount =
          transactionAmount >= 0 ? (value as number) * 100 : -(value as number) * 100;
        newSplitTransactions[index] = {
          ...newSplitTransactions[index],
          amount: signedAmount,
        };
      } else {
        newSplitTransactions[index] = {
          ...newSplitTransactions[index],
          description: value as string,
        };
      }

      return {
        ...prevTns,
        splitTransactions: newSplitTransactions,
      };
    });
  };

  const handleSplitSave = () => {
    if (!transaction.splitTransactions) return;

    // Instead of directly creating transactions, pass them to the parent for individual assignment
    // This will trigger the AssignSplitTransactionModal flow
    onSplitSuccess?.(transaction.splitTransactions);
  };

  const handleSave = () => {
    // Derive participating members from non-zero member values (selection UI removed)
    const participatingMembers = Object.entries(transaction.memberValues)
      .filter(([, value]) => (value || 0) !== 0)
      .map(([id]) => +id);
    const memberSplits: BankTransactionParticipation[] = participatingMembers.map((memberId) => {
      return {
        connectionId: memberId,
        amount: transaction.memberValues[memberId] || 0,
      };
    });

    // TODO call onSplitError
    if (!transaction.transactionType || Number.isNaN(transaction.amount)) return;
    if (
      transaction.transactionType === TransactionType.CONTRIBUTION &&
      transaction.isContributionPayment === 'YES' &&
      (!transaction.contributionPayment ||
        !transaction.contributionPayment.connectionId ||
        !transaction.contributionPayment.contributionPlanId)
    )
      return;

    // Prevent invalid personal transactions without participations
    if (
      (transaction.transactionType === TransactionType.PERSONAL_EXPENSE ||
        transaction.transactionType === TransactionType.PERSONAL_INCOME) &&
      memberSplits.length === 0
    ) {
      return;
    }

    const transactionDto: CreateBankTransactionModel = {
      amount: transaction.amount,
      description: transaction.description,
      date: transaction.date,
      mutationId: transaction.mutationId,
      payerId: transaction.payerId,
      transactionType: transaction.transactionType,
      participations:
        transaction.assignmentType === 'INDIVIDUALS' &&
        transaction.transactionType !== TransactionType.CONTRIBUTION
          ? memberSplits.length > 0
            ? memberSplits
            : undefined
          : undefined,
      contributionPayment:
        transaction.transactionType === TransactionType.CONTRIBUTION &&
        transaction.isContributionPayment === 'YES'
          ? {
              contributionPlanId: transaction.contributionPayment!.contributionPlanId!,
              connectionId: transaction.contributionPayment!.connectionId!,
            }
          : undefined,
    };

    onSplitSuccess?.(transactionDto);
  };

  // Amount formatting handled within SplitMembersList

  const getSplitRemainingAmount = () => {
    if (!transaction.splitTransactions) return 0;

    const totalSplitAmount = transaction.splitTransactions.reduce(
      (sum, tns) => sum + (tns.amount || 0),
      0,
    );
    return transactionAmount - totalSplitAmount;
  };

  // Select-all is replaced by Split/Reset actions

  // Split equally among selected (or all) members
  const splitEqually = () => {
    setTransaction((prevTns) => {
      const selected = prevTns.selectedMembers.length
        ? prevTns.selectedMembers
        : connections.map((c) => +c.id);

      if (selected.length === 0) return prevTns;

      const total = prevTns.amount;
      const sign = total < 0 ? -1 : 1;
      const totalAbs = Math.abs(total);
      const equalAbs = Math.floor(totalAbs / selected.length);
      const remainder = totalAbs % selected.length;

      const memberValues: Record<number, number> = {};
      const memberRations: Record<number, number | undefined> = {};
      selected.forEach((id, index) => {
        memberValues[id] = sign * (equalAbs + (index < remainder ? 1 : 0));
        memberRations[id] = undefined;
      });

      return {
        ...prevTns,
        selectedMembers: selected,
        memberValues,
        memberRations,
      };
    });
  };

  // Clear all selections and values
  const resetSplit = () => {
    setTransaction((prevTns) => ({
      ...prevTns,
      selectedMembers: [],
      memberValues: {},
      memberRations: {},
    }));
  };

  const isResetable = useMemo(
    () => Object.values(transaction.memberValues).some((v) => Math.abs(v || 0) > 0),
    [transaction.memberValues],
  );

  if (!transaction) {
    return null;
  }

  // Get transaction info from mutation or initial transaction
  const transactionInfo = mutation || initialTransaction;
  const transactionAmount = transactionInfo?.amount ?? 0;
  const transactionDate = mutation
    ? new Date(mutation.transactionDate)
    : initialTransaction?.date || new Date();
  const transactionDescription = mutation?.description || initialTransaction?.description || '';

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl rounded-lg" closeButton>
      <div className="max-h-[calc(100vh-5rem)] overflow-y-auto rounded-md bg-secondary-50 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-semibold">
            {editMode ? t('editTransaction') : t('addTransaction')}
          </h3>
        </div>

        {/* Transaction Information Header */}
        <div className="mb-6 rounded-lg bg-white p-4 shadow-sm">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">{t('date')}</p>
              <p className="text-base font-semibold">{formatDate(transactionDate)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('description')}</p>
              <p className="text-base font-semibold">{transactionDescription}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('amount')}</p>
              <p className="text-base font-semibold">
                {formatEuroCentsWithSign(transactionAmount, i18n.language)}
              </p>
            </div>
          </div>
          {/* Processor Information */}
          {initialTransaction &&
            'processedByConnection' in initialTransaction &&
            'transactionId' in initialTransaction && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-500">{t('processedBy')}:</p>
                  <TransactionProcessor transaction={initialTransaction} />
                </div>
              </div>
            )}
        </div>

        {/* Internal transfer badge and counterpart navigation removed */}

        {/* Process Type Slider */}
        <div className="mb-6">
          <Slider
            label={t('processLabel')}
            items={[
              { label: t('processAssign'), value: 'ASSIGN' as ProcessType },
              { label: t('processSplit'), value: 'SPLIT' as ProcessType },
              { label: t('processInternal'), value: 'INTERNAL' as ProcessType },
            ]}
            value={transaction.processType}
            onChange={handleProcessTypeChange}
          />
        </div>

        {/* Split Functionality */}
        {transaction.processType === 'SPLIT' && (
          <div className="mb-6">
            {/* Transaction details */}
            {transaction.splitTransactions && (
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 text-lg font-medium">{t('transactionViewSubtitle')}</h4>
                  <p className="mb-4 text-sm text-gray-600">
                    Fill in the details for each transaction. The total amount must equal the
                    original mutation amount.
                  </p>
                </div>

                <div className="space-y-4">
                  {transaction.splitTransactions.map((splitTransaction, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 rounded-lg border border-gray-200 p-4">
                      <div className="flex-shrink-0">
                        <span className="text-sm font-medium text-gray-700">
                          {t('transactionN', { number: index + 1 })}
                        </span>
                      </div>

                      <div className="flex-1">
                        <input
                          type="text"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder={t('descriptionPlaceholder')}
                          value={splitTransaction.description || ''}
                          onChange={(e) =>
                            handleSplitTransactionChange(index, 'description', e.target.value)
                          }
                        />
                      </div>

                      <div className="w-32">
                        <CurrencyInput
                          type="text"
                          className="w-full"
                          handleChange={(value) =>
                            handleSplitTransactionChange(index, 'amount', value)
                          }
                          value={
                            splitTransaction.amount
                              ? `€${(splitTransaction.amount / 100).toFixed(2)}`
                              : undefined
                          }
                          debounced
                        />
                      </div>

                      {/* Remove button - only show if more than 2 transactions */}
                      {transaction.splitTransactions &&
                        transaction.splitTransactions.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeSplitTransaction(index)}
                            className="flex-shrink-0 rounded-md p-2 text-danger transition-colors hover:bg-red-50 hover:text-red-700"
                            title="Remove transaction">
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                    </div>
                  ))}

                  {/* Add transaction button */}
                  <div className="flex justify-center">
                    <Button
                      onClick={addSplitTransaction}
                      variant="primary-dark"
                      className="hover:bg-secondary-300 bg-secondary-200 text-primary-900">
                      {t('addAnotherTransaction')}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-md bg-secondary-200 bg-opacity-50 p-3">
                  <span className="font-medium">{t('remaining')}:</span>
                  <span
                    className={`font-medium ${getSplitRemainingAmount() === 0 ? 'text-success' : 'text-danger'}`}>
                    €{(getSplitRemainingAmount() / 100).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Internal transfer controls */}
        {transaction.processType === 'INTERNAL' && (
          <div className="mb-6 rounded-lg bg-white p-4 shadow-sm">
            {/* Empty state if only one bank account */}
            {bankAccountsCount !== undefined && bankAccountsCount <= 1 && (
              <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-4 text-sm text-primary-900">
                {t('internalNotPossibleSingleAccount')}
              </div>
            )}

            {/* No candidates message */}
            {bankAccountsCount !== undefined &&
              bankAccountsCount > 1 &&
              (transaction.internalCandidates?.length || 0) === 0 && (
                <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-3 text-sm text-primary-900">
                  {t('noInternalCandidates')}
                </div>
              )}

            {counterpartTransaction && (
              <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-3">
                <p className="mb-1 text-xs font-medium text-gray-500">Linked counterpart</p>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="rounded bg-secondary-200 px-2 py-0.5 text-primary-900">
                    #{counterpartTransaction.transactionId}
                  </span>
                  <span>{formatDate(new Date(counterpartTransaction.date))}</span>
                  <span>
                    {formatEuroCentsWithSign(counterpartTransaction.amount, i18n.language)}
                  </span>
                  <span className="text-gray-700">{counterpartTransaction.description}</span>
                </div>
              </div>
            )}

            {/* Description editor */}
            <div className="mb-4">
              <Labeled label={t('description')} htmlFor="internal-transaction-description-textarea">
                <textarea
                  className="h-[unset] w-full"
                  rows={2}
                  id="internal-transaction-description-textarea"
                  placeholder={t('descriptionPlaceholder')}
                  value={transaction.description}
                  onChange={(ev) =>
                    setTransaction((prevTns) => ({
                      ...prevTns,
                      description: ev.target.value,
                    }))
                  }
                />
              </Labeled>
            </div>
            {/* Exactly one candidate - show summary, no dropdown */}
            {/* {bankAccountsCount !== undefined &&
              bankAccountsCount > 1 &&
              (transaction.internalCandidates?.length || 0) === 1 && (
                <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-3">
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    {t('counterpartMutation')}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span>
                      {formatDate(
                        new Date(
                          transaction.internalCandidates![0].transactionDate as unknown as Date,
                        ),
                      )}
                    </span>
                    <span>
                      {formatEuroCentsWithSign(
                        transaction.internalCandidates![0].amount,
                        i18n.language,
                      )}
                    </span>
                    <span className="text-gray-700">
                      {transaction.internalCandidates![0].description}
                    </span>
                  </div>
                </div>
              )} */}
            {/* Counterpart selection (hidden in edit mode for existing internal transfers). Hide single-candidate summary in edit mode too. */}
            {(() => {
              const existing = initialTransaction as unknown as
                | { transactionType?: TransactionType }
                | undefined;
              const isEditingInternal =
                !!existing && existing.transactionType === TransactionType.INTERNAL_BANK_TRANSFER;
              const candidatesCount = transaction.internalCandidates?.length || 0;
              if (isEditingInternal) return null;
              if (candidatesCount === 1) {
                return (
                  <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-3">
                    <p className="mb-1 text-xs font-medium text-gray-500">{t('member')}</p>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span>
                        {formatDate(
                          new Date(
                            transaction.internalCandidates![0].transactionDate as unknown as Date,
                          ),
                        )}
                      </span>
                      <span>
                        {formatEuroCentsWithSign(
                          transaction.internalCandidates![0].amount,
                          i18n.language,
                        )}
                      </span>
                      <span className="text-gray-700">
                        {transaction.internalCandidates![0].description}
                      </span>
                    </div>
                  </div>
                );
              }
              return (
                <div className="mt-4">
                  <Labeled label="Counterpart mutation">
                    <DropdownSelect
                      options={(transaction.internalCandidates ?? []).map((m) => ({
                        label: `${formatDate(new Date(m.transactionDate))} • ${m.oppositeName} • ${formatEuroCentsWithSign(m.amount, i18n.language)}`,
                        value: m.mutationId,
                      }))}
                      setValue={(val) =>
                        setTransaction((prev) => ({
                          ...prev,
                          internalSelectedCounterpartId: val || undefined,
                        }))
                      }
                      value={transaction.internalSelectedCounterpartId}
                    />
                  </Labeled>
                </div>
              );
            })()}
          </div>
        )}

        {/* Assign Flow - Show existing TransactionModal content */}
        {transaction.processType === 'ASSIGN' && (
          <div className="mb-4">
            <div className="mb-2 flex flex-row gap-4">
              <div className="flex-1">
                <Labeled label={t('description')} htmlFor="transaction-description-textarea">
                  <textarea
                    className="h-[unset] w-full"
                    rows={2}
                    id="transaction-description-textarea"
                    placeholder={t('descriptionPlaceholder')}
                    value={transaction.description}
                    onChange={(ev) =>
                      setTransaction((prevTns) => ({
                        ...prevTns,
                        description: ev.target.value,
                      }))
                    }
                  />
                </Labeled>
              </div>
            </div>

            {/* Transaction Type Selection with Sliders */}
            <div className="mb-6">
              {/* First Slider - Assignment Type */}
              <div className="mb-4">
                <Slider
                  label={t('assignmentTypeLabel', {
                    type:
                      transaction.amount < 0
                        ? t('expense').toLocaleLowerCase()
                        : t('income').toLocaleLowerCase(),
                  })}
                  items={[
                    { label: t('entireGroup'), value: 'ENTIRE_GROUP' as AssignmentType },
                    { label: t('individuals'), value: 'INDIVIDUALS' as AssignmentType },
                  ]}
                  value={transaction.assignmentType}
                  onChange={handleAssignmentTypeChange}
                />
              </div>

              {/* Second Slider - Contribution Payment (only for income with individuals and when plans exist) */}
              {transaction.amount > 0 &&
                transaction.assignmentType === 'INDIVIDUALS' &&
                Object.keys(plans).length > 0 && (
                  <div className="mb-4">
                    <Slider
                      label={t('contributionPaymentQuestion')}
                      items={[
                        { label: t('no'), value: 'NO' as ContributionType },
                        { label: t('yes'), value: 'YES' as ContributionType },
                      ]}
                      value={transaction.isContributionPayment}
                      onChange={handleContributionTypeChange}
                    />
                  </div>
                )}
            </div>

            {transaction.transactionType === TransactionType.CONTRIBUTION &&
              transaction.isContributionPayment === 'YES' && (
                <div className="space-y-4">
                  <Labeled label={t('member')}>
                    <DropdownSelect
                      options={connectionsWithPlans.map((c) => ({
                        label: `${c.fields.FIRST_NAME} ${c.fields.LAST_NAME}`,
                        value: +c.id,
                      }))}
                      setValue={(connectionId) =>
                        setTransaction((prevTns) => ({
                          ...prevTns,
                          contributionPayment: {
                            ...(prevTns.contributionPayment ?? {}),
                            connectionId,
                            // Clear contribution plan when connection changes
                            contributionPlanId: undefined,
                          },
                        }))
                      }
                      value={transaction.contributionPayment?.connectionId}
                    />
                  </Labeled>

                  {transaction.contributionPayment?.connectionId && (
                    <Labeled label={t('plan')}>
                      <DropdownSelect
                        options={getContributionPlansForConnection(
                          transaction.contributionPayment.connectionId,
                        ).map((p) => ({
                          label: p.name,
                          value: p.contributionPlanId,
                        }))}
                        setValue={(contributionPlanId) =>
                          setTransaction((prevTns) => ({
                            ...prevTns,
                            contributionPayment: {
                              ...prevTns.contributionPayment,
                              contributionPlanId,
                            },
                          }))
                        }
                        value={transaction.contributionPayment?.contributionPlanId}
                      />
                    </Labeled>
                  )}
                </div>
              )}

            {transaction.assignmentType === 'INDIVIDUALS' &&
              transaction.transactionType !== TransactionType.CONTRIBUTION && (
                <SplitMembersList
                  members={connections.map((m) => ({
                    id: +m.id,
                    label: `${m.fields.FIRST_NAME} ${m.fields.LAST_NAME}`,
                  }))}
                  memberValues={transaction.memberValues}
                  memberRations={transaction.memberRations}
                  onSetValue={(id, v) => handleValueChange(id, v)}
                  onSetRations={(id, r) =>
                    setTransaction((prevTns) =>
                      recalcFromRations(prevTns, {
                        memberRations: { ...(prevTns.memberRations ?? {}), [id]: r },
                      }),
                    )
                  }
                  headerTitle={t('selectMembers')}
                  onSplit={splitEqually}
                  onClear={resetSplit}
                  isResetable={isResetable}
                  remainingAmount={remainingAmount}
                  remainingLabel={t('remaining')}
                />
              )}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="primary-dark"
            className="bg-secondary-200 text-primary-900"
            onClick={() => setOpen(false)}>
            {t('cancelButton')}
          </Button>

          {/* Split save button */}
          {transaction.processType === 'SPLIT' && transaction.splitTransactions && (
            <Button
              variant="primary-dark"
              onClick={handleSplitSave}
              disabled={
                !transaction.splitTransactions ||
                transaction.splitTransactions.some(
                  (tns) => !tns.description || (tns.amount || 0) === 0,
                ) ||
                getSplitRemainingAmount() !== 0
              }>
              {t('confirmSplit')}
            </Button>
          )}

          {/* Assign save button */}
          {transaction.processType === 'ASSIGN' && (
            <Button
              variant="primary-dark"
              onClick={handleSave}
              disabled={
                !transaction.transactionType ||
                !transaction.amount ||
                (transaction.assignmentType === 'INDIVIDUALS' && remainingAmount !== 0) ||
                (transaction.transactionType === TransactionType.CONTRIBUTION &&
                  transaction.isContributionPayment === 'YES' &&
                  (!transaction.contributionPayment ||
                    !transaction.contributionPayment.connectionId ||
                    !transaction.contributionPayment.contributionPlanId))
              }>
              {t('assignButton')}
            </Button>
          )}

          {/* Internal save button */}
          {transaction.processType === 'INTERNAL' && (
            <Button
              variant="primary-dark"
              onClick={handleInternalSave}
              disabled={(() => {
                const existing = initialTransaction as unknown as
                  | { transactionType?: TransactionType }
                  | undefined;
                const isEditingInternal =
                  !!existing && existing.transactionType === TransactionType.INTERNAL_BANK_TRANSFER;
                return isEditingInternal || !transaction.internalSelectedCounterpartId;
              })()}>
              {t('assignButton')}
            </Button>
          )}
        </div>
      </div>
    </ModalLayout>
  );
}

// Checkbox Component
// Checkbox removed from UI
